import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import notificationService from '../../../services/notificationService';
import realtimePresenceService from '../../../services/realtimePresenceService';
import { useAuth } from '../../../contexts/AuthContext';

const PartnerAvailabilityToggle = ({ isVisible, onToggle, partnerName }) => {
  const { userProfile } = useAuth();
  const [previousVisibility, setPreviousVisibility] = React.useState(isVisible);
  const [partnerAvailability, setPartnerAvailability] = useState(false);

  // Real-time partner availability subscription
  useEffect(() => {
    if (!userProfile?.partner_id) return;

    const partnerId = userProfile?.partner_id;

    // Fetch initial availability
    const loadPartnerAvailability = async () => {
      const presenceData = await realtimePresenceService?.fetchPartnerPresence(partnerId);
      if (presenceData) {
        setPartnerAvailability(presenceData?.isActive || false);
      }
    };

    loadPartnerAvailability();

    // Subscribe to real-time availability changes
    const channel = realtimePresenceService?.subscribeToPartnerAvailability(
      partnerId,
      (availabilityData) => {
        setPartnerAvailability(availabilityData?.isActive || false);
        
        // Trigger intelligent notification when partner becomes available
        if (availabilityData?.isActive && !availabilityData?.wasActive) {
          const availabilityContext = {
            newSlots: ['Tonight 8-10pm', 'Tomorrow 2-4pm'],
            userAvailability: ['flexible evenings', 'weekend mornings'],
          };

          const scheduleHistory = {
            typicalAvailability: ['evenings', 'weekends'],
            successfulTimes: ['Friday evenings', 'Sunday mornings'],
          };

          const relationshipContext = {
            compatibilityScore: 85,
            moodAlignment: 'high',
          };

          notificationService?.notifyIntelligentAvailability(
            availabilityContext,
            scheduleHistory,
            relationshipContext
          );
        }
      }
    );

    // Cleanup subscription
    return () => {
      if (channel) {
        realtimePresenceService?.unsubscribe(`partner_availability_${partnerId}`);
      }
    };
  }, [userProfile?.partner_id]);

  // Trigger intelligent notification when partner availability changes
  React.useEffect(() => {
    if (previousVisibility !== isVisible && isVisible) {
      // Gather context for intelligent notification
      const availabilityData = {
        newSlots: ['Tonight 8-10pm', 'Tomorrow 2-4pm'],
        userAvailability: ['flexible evenings', 'weekend mornings'],
      };

      const scheduleHistory = {
        typicalAvailability: ['evenings', 'weekends'],
        successfulTimes: ['Friday evenings', 'Sunday mornings'],
      };

      const relationshipContext = {
        compatibilityScore: 85,
        moodAlignment: 'high',
      };

      // Send intelligent notification
      notificationService?.notifyIntelligentAvailability(
        availabilityData,
        scheduleHistory,
        relationshipContext
      );
    }
    setPreviousVisibility(isVisible);
  }, [isVisible, previousVisibility]);

  return (
    <button
      onClick={onToggle}
      className={
        `flex items-center space-x-2 px-4 py-2 rounded-md
        transition-therapeutic border
        ${isVisible
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-card text-foreground border-border hover:bg-muted'
        }`
      }
      aria-label={isVisible ? 'Hide partner availability' : 'Show partner availability'}
    >
      <Icon 
        name={isVisible ? 'EyeOff' : 'Eye'} 
        size={18} 
        color="currentColor"
      />
      <span className="text-sm font-medium font-caption hidden sm:inline">
        {isVisible ? `Hide ${partnerName}'s Availability` : `Show ${partnerName}'s Availability`}
      </span>
      <span className="text-sm font-medium font-caption sm:hidden">
        {isVisible ? 'Hide' : 'Show'} Availability
      </span>
      {partnerAvailability && (
        <span className="ml-1 w-2 h-2 rounded-full bg-success animate-pulse" title="Partner is available" />
      )}
    </button>
  );
};

export default PartnerAvailabilityToggle;